import { useCallback, useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';

import type { EntityToggleState } from '@/components/map/utils/mapLayers';
import { addOrbitLayer, ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import {
    computeStackOffsets,
    buildStackIconOffsetExpression,
    stackFanX,
} from '@/components/map/utils/computeStackOffsets';
import {
    BADGED_MARKER_HEIGHT,
    MARKER_WIDTH,
    SELECTED_MARKER_HEIGHT,
    SELECTED_MARKER_WIDTH,
    STACK_SEPARATION_PX,
    STANDARD_MARKER_HEIGHT,
    buildEntityMarkerImages,
    buildFanBaseImages,
    leakMarkerImageId,
    poiMarkerImageId,
} from '@/components/map/utils/entityMarkerArt';
import { LEAK_ENTITY_STATUSES, POI_ENTITY_STATUSES, normaliseEntityType } from '@/components/map/utils/entityStatus';
import { buildEntityClusterProperties } from '@/components/map/utils/entityClusterProperties';
import { showErrorToast } from '@/errors/showErrorToast';
import { attachViewportLoader, EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from '@/utils/mapUtils';
import { getMarkerPixelRatio, loadSvgAsImageData } from '@/utils/svgImage';
import { createHaloPulse } from '@/utils/haloPulse';
import type { HaloPulse } from '@/utils/haloPulse';

// Entity-mode map pipeline for the split POI / Find & Fix leak model. Renders two symbol
// families over the two shared clustered sources: POI entities on ORBIT_LAYER_IDS.pois and
// leak entities on ORBIT_LAYER_IDS.leaks, registered above the POI layer so the
// cross-family hit-test winner is deterministic by layer order alone (no symbol-sort-key —
// queryRenderedFeatures order within one symbol layer does not track sort-key paint order).
//
// Exactly-coincident stacks (a POI and its as-reported first leak are the routine case)
// are annotated by computeStackOffsets and fanned apart with a data-driven icon-offset —
// geometry is never moved, so cluster membership is untouched — with a shared-origin
// fan-base image (dot + hairline) rendered underneath from the same annotation.
//
// Supersedes the pre-split usePoisLayer (deleted at cleanup): symbol collision
// culling is disabled here (icon-allow-overlap true), so markers are no longer suppressed
// by overlapping neighbours or basemap labels; features can still be legitimately absent
// via the entity toggles, the Filters panel, the semantic-zoom band, clustering, or source
// membership.

const ENTITY_SOURCE_ID = 'orbit-pois-source';
export const ENTITY_NETWORK_SOURCE_ID = 'orbit-poi-network-source';

export const POI_MIN_ZOOM = 11;
export const MARKER_SIZE = STANDARD_MARKER_HEIGHT;

// Selected marker canvas geometry (see entityMarkerArt): centre anchor with the tip at
// (52, 84) → offset -12 lands the tip on the coordinate; the coincident-stack fan x-offset
// is composed into the same frame so a selected stack member's enlarged pin (and halos,
// via circle-translate) follow its fan position instead of snapping back to the shared
// coordinate.
const SELECTED_ICON_OFFSET_Y = -12;

const SELECTED_ENTITY_LAYER_IDS = [
    ORBIT_LAYER_IDS.poiSelectedHaloOuter,
    ORBIT_LAYER_IDS.poiSelectedHaloInner,
    ORBIT_LAYER_IDS.poiSelected,
] as const;

const HALO_LAYER_IDS = [ORBIT_LAYER_IDS.poiSelectedHaloOuter, ORBIT_LAYER_IDS.poiSelectedHaloInner] as const;

// Selection halo behaviour (matched to the pre-split pins so the interaction feels unchanged).
const HALO_INNER_RADIUS = 29;
const HALO_OUTER_RADIUS = 50;
const HALO_INNER_OPACITY = 0.35;
const HALO_OUTER_OPACITY = 0.2;
const HALO_PULSE_MIN_RADIUS = 2;
const HALO_PULSE_MAX_RADIUS = 52;
const HALO_PULSE_PEAK_OPACITY = 0.35;
const HALO_PULSE_CYCLE_MS = 1100;
const HALO_PULSE_OVERLAP_MS = 300;
const HALO_PULSE_PULSES = 3;
const HALO_PULSE_SETTLE_MS = 350;

export type { EntityToggleState } from '@/components/map/utils/mapLayers';

/**
 * Where the entity features come from. The hook owns the sources, layers, images and
 * selection highlight; the feeds decide whose records flow through them — one client's in
 * Find & Fix, every included client's in Network Explorer — so neither mode needs its own
 * copy of the pipeline. Callers must hand over a referentially stable object per feed set
 * (memoised on what it closes over): a new object is a new feed set.
 */
export interface EntityFeeds {
    /** Identity of the feed set. A change drops both cached feeds and refetches. */
    key: string;
    /** The records inside a viewport (`minLng,minLat,maxLng,maxLat`), from POI_MIN_ZOOM. */
    fetchViewport: (bbox: string) => Promise<FeatureCollection>;
    /**
     * Every record, backing the network-level clusters below POI_MIN_ZOOM. Absent when the
     * wide end is drawn by something else (the explorer's cells layer): the network source
     * and layers are still registered, and stay empty.
     */
    fetchNetwork?: () => Promise<FeatureCollection>;
}

// ─── Registry-driven expressions ───────────────────────────────────────────────────────

// The registry-driven expressions are assembled dynamically, which the style-spec's fixed
// tuple typings cannot express — built as unknown[] and cast, like the stack-offset
// expression builder.
function buildPoiIconExpression(selected: boolean): maplibregl.ExpressionSpecification {
    const suffix = selected ? '-selected' : '';
    const branches = POI_ENTITY_STATUSES.filter((d) => d.enabled).flatMap((d) => [
        d.status,
        `${poiMarkerImageId(d.status)}${suffix}`,
    ]);
    const expression: unknown[] = [
        'match',
        ['get', 'status'],
        ...branches,
        `${poiMarkerImageId('unclassified')}${suffix}`,
    ];
    return expression as maplibregl.ExpressionSpecification;
}

function buildLeakIconExpression(selected: boolean): maplibregl.ExpressionSpecification {
    const suffix = selected ? '-selected' : '';
    const branches = LEAK_ENTITY_STATUSES.filter((d) => d.enabled).flatMap((d) => [
        d.status,
        `${leakMarkerImageId(d.status)}${suffix}`,
    ]);
    const expression: unknown[] = [
        'match',
        ['get', 'status'],
        ...branches,
        `${leakMarkerImageId('unclassified')}${suffix}`,
    ];
    return expression as maplibregl.ExpressionSpecification;
}

function buildSelectedIconExpression(): maplibregl.ExpressionSpecification {
    const expression: unknown[] = [
        'case',
        ['==', ['get', 'entity_type'], 'leak'],
        buildLeakIconExpression(true),
        buildPoiIconExpression(true),
    ];
    return expression as maplibregl.ExpressionSpecification;
}

// Status → selection-halo tint, per family, from the registries.
function buildHaloColorExpression(): maplibregl.ExpressionSpecification {
    const poiBranches = POI_ENTITY_STATUSES.filter((d) => d.enabled).flatMap((d) => [
        d.status,
        // The awaiting grey pin would produce an invisible low-opacity halo; darken it.
        d.status === 'awaiting_assessment' ? '#6b7280' : d.haloColor,
    ]);
    const leakBranches = LEAK_ENTITY_STATUSES.filter((d) => d.enabled).flatMap((d) => [d.status, d.haloColor]);
    const expression: unknown[] = [
        'case',
        ['==', ['get', 'entity_type'], 'leak'],
        ['match', ['get', 'status'], ...leakBranches, '#6b7280'],
        ['match', ['get', 'status'], ...poiBranches, '#6b7280'],
    ];
    return expression as maplibregl.ExpressionSpecification;
}

const FAN_BASE_ICON_EXPRESSION: maplibregl.ExpressionSpecification = [
    'match',
    ['get', 'stack_size'],
    2,
    'entity-fan-base-2',
    3,
    'entity-fan-base-3',
    4,
    'entity-fan-base-4',
    'entity-fan-base-4',
];

const STACK_ICON_OFFSET = buildStackIconOffsetExpression(STACK_SEPARATION_PX, [0, 6]);
const SELECTED_STACK_ICON_OFFSET = buildStackIconOffsetExpression(STACK_SEPARATION_PX, [0, SELECTED_ICON_OFFSET_Y]);

// ─── Image registration ────────────────────────────────────────────────────────────────

async function addImage(
    mlMap: maplibregl.Map,
    id: string,
    svg: string,
    width: number,
    height: number,
    pixelRatio: number,
): Promise<void> {
    if (mlMap.hasImage(id)) return;
    try {
        const imageData = await loadSvgAsImageData(svg, width, height, pixelRatio);
        if (!mlMap.hasImage(id)) mlMap.addImage(id, imageData, { pixelRatio });
    } catch {
        // Progressive enhancement — a missing marker image falls back to the default icon.
    }
}

export async function registerEntityImages(mlMap: maplibregl.Map): Promise<void> {
    const pixelRatio = getMarkerPixelRatio();
    for (const image of buildEntityMarkerImages()) {
        await addImage(mlMap, image.id, image.svg, image.width, image.height, pixelRatio);
    }
    for (const image of buildFanBaseImages()) {
        await addImage(mlMap, image.id, image.svg, image.width, image.height, pixelRatio);
    }
}

// ─── Source + layer registration ───────────────────────────────────────────────────────

function registerViewportSourceAndLayers(mlMap: maplibregl.Map, data: FeatureCollection): void {
    try {
        if (mlMap.getSource(ENTITY_SOURCE_ID)) {
            (mlMap.getSource(ENTITY_SOURCE_ID) as maplibregl.GeoJSONSource).setData(data);
        } else {
            mlMap.addSource(ENTITY_SOURCE_ID, {
                type: 'geojson',
                data,
                cluster: true,
                clusterMaxZoom: 16,
                clusterRadius: 50,
                // clusterProperties is immutable after addSource — part of this spec only.
                clusterProperties: buildEntityClusterProperties(),
            });
        }
    } catch {
        return;
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.poiClusters)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.poiClusters,
                    type: 'circle',
                    source: ENTITY_SOURCE_ID,
                    filter: ['has', 'point_count'],
                    minzoom: POI_MIN_ZOOM,
                    paint: {
                        'circle-color': '#3b82f6',
                        'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 100, 24],
                        'circle-stroke-color': '#ffffff',
                        'circle-stroke-width': 2,
                    },
                },
                'poiClusters',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.poiClusterCount)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.poiClusterCount,
                    type: 'symbol',
                    source: ENTITY_SOURCE_ID,
                    filter: ['has', 'point_count'],
                    minzoom: POI_MIN_ZOOM,
                    layout: {
                        'text-field': ['get', 'point_count_abbreviated'],
                        // A font the OpenFreeMap glyphs endpoint hosts (the default 404s).
                        'text-font': ['Noto Sans Regular'],
                        'text-size': 12,
                        'text-allow-overlap': true,
                    },
                    paint: { 'text-color': '#ffffff' },
                },
                'poiClusters',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    // Shared-origin fan base FIRST (within-group order = registration order), then POI
    // entities, then leak entities on top.
    if (!mlMap.getLayer(ORBIT_LAYER_IDS.entityFanBase)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.entityFanBase,
                    type: 'symbol',
                    source: ENTITY_SOURCE_ID,
                    // One base per stack: drawn for the member at fan position 0 only (all
                    // stack members share the exact coordinate, so any single member places
                    // the image correctly at the true location).
                    filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'stack_index'], 0]],
                    minzoom: POI_MIN_ZOOM,
                    layout: {
                        'icon-image': FAN_BASE_ICON_EXPRESSION,
                        'icon-anchor': 'bottom',
                        // Same tip convention as the markers: the dot/hairline row sits 6px
                        // above the canvas bottom, landing exactly on the coordinate.
                        'icon-offset': [0, 6] as [number, number],
                        'icon-size': 1,
                        'icon-allow-overlap': true,
                    },
                },
                'pois',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.pois)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.pois,
                    type: 'symbol',
                    source: ENTITY_SOURCE_ID,
                    filter: ['all', ['!', ['has', 'point_count']], ['!=', ['get', 'entity_type'], 'leak']],
                    minzoom: POI_MIN_ZOOM,
                    layout: {
                        'icon-image': buildPoiIconExpression(false),
                        'icon-anchor': 'bottom',
                        'icon-offset': STACK_ICON_OFFSET,
                        'icon-size': 1,
                        'icon-allow-overlap': true,
                    },
                },
                'pois',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.leaks)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.leaks,
                    type: 'symbol',
                    source: ENTITY_SOURCE_ID,
                    filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'entity_type'], 'leak']],
                    minzoom: POI_MIN_ZOOM,
                    layout: {
                        'icon-image': buildLeakIconExpression(false),
                        'icon-anchor': 'bottom',
                        'icon-offset': STACK_ICON_OFFSET,
                        'icon-size': 1,
                        'icon-allow-overlap': true,
                    },
                },
                'pois',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.poiSelectedHaloOuter)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.poiSelectedHaloOuter,
                    type: 'circle',
                    source: ENTITY_SOURCE_ID,
                    filter: ['==', ['get', 'id'], ''],
                    minzoom: POI_MIN_ZOOM,
                    paint: {
                        'circle-radius': 0,
                        'circle-radius-transition': { duration: 0 },
                        'circle-opacity-transition': { duration: 0 },
                        'circle-color': buildHaloColorExpression(),
                        'circle-opacity': HALO_OUTER_OPACITY,
                    },
                },
                'interaction',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.poiSelectedHaloInner)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.poiSelectedHaloInner,
                    type: 'circle',
                    source: ENTITY_SOURCE_ID,
                    filter: ['==', ['get', 'id'], ''],
                    minzoom: POI_MIN_ZOOM,
                    paint: {
                        'circle-radius': 0,
                        'circle-radius-transition': { duration: 0 },
                        'circle-opacity-transition': { duration: 0 },
                        'circle-color': buildHaloColorExpression(),
                        'circle-opacity': HALO_INNER_OPACITY,
                    },
                },
                'interaction',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.poiSelected)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.poiSelected,
                    type: 'symbol',
                    source: ENTITY_SOURCE_ID,
                    filter: ['==', ['get', 'id'], ''],
                    minzoom: POI_MIN_ZOOM,
                    layout: {
                        'icon-image': buildSelectedIconExpression(),
                        'icon-anchor': 'center',
                        // The stack fan is composed into the selected frame: an offset
                        // member's enlarged pin follows its fan position.
                        'icon-offset': SELECTED_STACK_ICON_OFFSET,
                        'icon-size': 1,
                        'icon-allow-overlap': true,
                    },
                },
                'interaction',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }
}

function registerNetworkSourceAndLayers(mlMap: maplibregl.Map, data: FeatureCollection): void {
    try {
        if (mlMap.getSource(ENTITY_NETWORK_SOURCE_ID)) {
            (mlMap.getSource(ENTITY_NETWORK_SOURCE_ID) as maplibregl.GeoJSONSource).setData(data);
        } else {
            mlMap.addSource(ENTITY_NETWORK_SOURCE_ID, {
                type: 'geojson',
                data,
                cluster: true,
                clusterMaxZoom: POI_MIN_ZOOM,
                clusterRadius: 50,
                maxzoom: 12,
                clusterProperties: buildEntityClusterProperties(),
            });
        }
    } catch {
        return;
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.poiNetworkClusters)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.poiNetworkClusters,
                    type: 'circle',
                    source: ENTITY_NETWORK_SOURCE_ID,
                    filter: ['has', 'point_count'],
                    maxzoom: POI_MIN_ZOOM,
                    paint: {
                        'circle-color': '#3b82f6',
                        'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 100, 24],
                        'circle-stroke-color': '#ffffff',
                        'circle-stroke-width': 2,
                    },
                },
                'poiClusters',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.poiNetworkUnclustered)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.poiNetworkUnclustered,
                    type: 'circle',
                    source: ENTITY_NETWORK_SOURCE_ID,
                    filter: ['!', ['has', 'point_count']],
                    maxzoom: POI_MIN_ZOOM,
                    paint: {
                        'circle-color': '#3b82f6',
                        'circle-radius': 16,
                        'circle-stroke-color': '#ffffff',
                        'circle-stroke-width': 2,
                    },
                },
                'poiClusters',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(ORBIT_LAYER_IDS.poiNetworkClusterCount)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: ORBIT_LAYER_IDS.poiNetworkClusterCount,
                    type: 'symbol',
                    source: ENTITY_NETWORK_SOURCE_ID,
                    maxzoom: POI_MIN_ZOOM,
                    layout: {
                        'text-field': ['to-string', ['coalesce', ['get', 'point_count_abbreviated'], '1']],
                        'text-font': ['Noto Sans Regular'],
                        'text-size': 12,
                        'text-allow-overlap': true,
                    },
                    paint: { 'text-color': '#ffffff' },
                },
                'poiClusters',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }
}

// ─── The hook ──────────────────────────────────────────────────────────────────────────

function filterByEntityToggles(data: FeatureCollection, toggles: EntityToggleState): FeatureCollection {
    if (toggles.pois && toggles.leaks) return data;
    if (!toggles.pois && !toggles.leaks) return EMPTY_FEATURE_COLLECTION;
    const keep = toggles.leaks ? 'leak' : 'poi';
    return {
        type: 'FeatureCollection',
        features: data.features.filter((f) => normaliseEntityType(f.properties?.['entity_type'] as string) === keep),
    };
}

function findStackTranslate(collections: FeatureCollection[], entityId: string): [number, number] {
    for (const collection of collections) {
        const feature = collection.features.find((f) => f.properties?.['id'] === entityId);
        if (feature) {
            const index: unknown = feature.properties?.['stack_index'];
            const size: unknown = feature.properties?.['stack_size'];
            if (typeof index === 'number' && typeof size === 'number') {
                return [stackFanX(index, size, STACK_SEPARATION_PX), 0];
            }
            return [0, 0];
        }
    }
    return [0, 0];
}

/**
 * Loads the entity-shaped map feeds, registers the entity sources, layers and images, keeps
 * them alive across basemap style reloads, and drives the selected-entity highlight (shared
 * id filter across both families — `leak:` ids are opaque and never parsed).
 *
 * `filterFeatures` is the Filters-panel hook-point: it receives the toggled-down
 * collection and returns the visible one. Stack annotation runs AFTER filtering, so a fan
 * only forms among markers that are actually visible.
 */
export function useEntitiesLayer(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    feeds: EntityFeeds | null,
    entityToggles: EntityToggleState,
    selectedEntityId: string | null = null,
    filterFeatures: (data: FeatureCollection) => FeatureCollection = (data) => data,
    enabled: boolean = true,
    interactionsEnabled: boolean = true,
): void {
    const viewportRawRef = useRef<FeatureCollection>(EMPTY_FEATURE_COLLECTION);
    const networkRawRef = useRef<FeatureCollection>(EMPTY_FEATURE_COLLECTION);
    // Last processed (visible) collections, for selected-stack translate lookup.
    const viewportVisibleRef = useRef<FeatureCollection>(EMPTY_FEATURE_COLLECTION);
    const networkVisibleRef = useRef<FeatureCollection>(EMPTY_FEATURE_COLLECTION);

    const entityTogglesRef = useRef(entityToggles);
    useEffect(() => {
        entityTogglesRef.current = entityToggles;
    }, [entityToggles]);

    const filterFeaturesRef = useRef(filterFeatures);
    useEffect(() => {
        filterFeaturesRef.current = filterFeatures;
    }, [filterFeatures]);

    const selectedEntityIdRef = useRef(selectedEntityId);
    useEffect(() => {
        selectedEntityIdRef.current = selectedEntityId;
    }, [selectedEntityId]);

    const haloPulseRef = useRef<HaloPulse | null>(null);
    const haloPlayedForRef = useRef<string | null>(null);

    // Deliberately NOT a fetch-effect dependency: the entity toggles are display switches,
    // applied by the re-filter effect over the cached raw feeds. Keying the fetches on them
    // would discard both caches on every off/on flick and re-issue the whole-client network
    // feed — the heaviest request the map makes — for a purely visual change.

    // Toggle gate → Filters-panel transform → stack annotation. Read through refs so the
    // stable callback survives inside effects and the viewport loader without stale closures.
    const processViewport = useCallback((data: FeatureCollection): FeatureCollection => {
        const toggled = filterByEntityToggles(data, entityTogglesRef.current);
        const filtered = filterFeaturesRef.current(toggled);
        const processed: FeatureCollection = {
            type: 'FeatureCollection',
            features: computeStackOffsets(filtered.features),
        };
        viewportVisibleRef.current = processed;
        return processed;
    }, []);

    const processNetwork = useCallback((data: FeatureCollection): FeatureCollection => {
        const toggled = filterByEntityToggles(data, entityTogglesRef.current);
        const filtered = filterFeaturesRef.current(toggled);
        const processed: FeatureCollection = {
            type: 'FeatureCollection',
            features: computeStackOffsets(filtered.features),
        };
        networkVisibleRef.current = processed;
        return processed;
    }, []);

    // Viewport feed: loads on moveend from POI_MIN_ZOOM.
    useEffect(() => {
        viewportRawRef.current = EMPTY_FEATURE_COLLECTION;
        const mlMap = mapRef.current?.getMap();
        if (mlMap) setGeoJsonSourceData(mlMap, ENTITY_SOURCE_ID, EMPTY_FEATURE_COLLECTION);

        if (!enabled) return;
        if (!feeds || !isMapLoaded) return;

        return attachViewportLoader(mapRef, {
            sourceId: ENTITY_SOURCE_ID,
            minZoom: POI_MIN_ZOOM,
            clientId: feeds.key,
            dataRef: viewportRawRef,
            fetchFn: (_key, bbox) => feeds.fetchViewport(bbox),
            transformFn: processViewport,
        });
    }, [feeds, isMapLoaded, enabled, mapRef, processViewport]);

    // Network feed: one fetch per feed set, backing the network-level clusters.
    useEffect(() => {
        networkRawRef.current = EMPTY_FEATURE_COLLECTION;
        const mlMap = mapRef.current?.getMap();
        if (mlMap) setGeoJsonSourceData(mlMap, ENTITY_NETWORK_SOURCE_ID, EMPTY_FEATURE_COLLECTION);

        if (!enabled) return;
        if (!feeds?.fetchNetwork || !isMapLoaded) return;

        let cancelled = false;
        feeds
            .fetchNetwork()
            .then((data) => {
                if (cancelled) return;
                networkRawRef.current = data;
                const map = mapRef.current?.getMap();
                if (map) setGeoJsonSourceData(map, ENTITY_NETWORK_SOURCE_ID, processNetwork(data));
            })
            .catch((err: unknown) => {
                if (!cancelled) showErrorToast(err);
            });
        return () => {
            cancelled = true;
        };
    }, [feeds, isMapLoaded, enabled, mapRef, processNetwork]);

    // Re-filter both sources when toggles or the filter transform change.
    useEffect(() => {
        if (!enabled) return;
        const map = mapRef.current?.getMap();
        if (!map) return;
        setGeoJsonSourceData(map, ENTITY_SOURCE_ID, processViewport(viewportRawRef.current));
        setGeoJsonSourceData(map, ENTITY_NETWORK_SOURCE_ID, processNetwork(networkRawRef.current));
    }, [entityToggles, filterFeatures, enabled, mapRef, processViewport, processNetwork]);

    // Register images, sources and layers; survive style reloads; cluster interactions.
    useEffect(() => {
        if (!enabled || !isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const register = () => {
            registerViewportSourceAndLayers(mlMap, processViewport(viewportRawRef.current));
            registerNetworkSourceAndLayers(mlMap, processNetwork(networkRawRef.current));
            void registerEntityImages(mlMap).catch(() => {
                // Progressive enhancement — missing images fall back to the default icon.
            });
            const id = selectedEntityIdRef.current;
            for (const layerId of SELECTED_ENTITY_LAYER_IDS) {
                if (mlMap.getLayer(layerId)) {
                    mlMap.setFilter(layerId, id ? ['==', ['get', 'id'], id] : ['==', ['get', 'id'], '']);
                }
            }
            if (id) {
                const translate = findStackTranslate([viewportVisibleRef.current, networkVisibleRef.current], id);
                for (const layerId of HALO_LAYER_IDS) {
                    if (mlMap.getLayer(layerId)) mlMap.setPaintProperty(layerId, 'circle-translate', translate);
                }
                if (mlMap.getLayer(ORBIT_LAYER_IDS.poiSelectedHaloInner)) {
                    mlMap.setPaintProperty(ORBIT_LAYER_IDS.poiSelectedHaloInner, 'circle-radius', HALO_INNER_RADIUS);
                }
                if (mlMap.getLayer(ORBIT_LAYER_IDS.poiSelectedHaloOuter)) {
                    mlMap.setPaintProperty(ORBIT_LAYER_IDS.poiSelectedHaloOuter, 'circle-radius', HALO_OUTER_RADIUS);
                }
            }
        };

        register();
        mlMap.on('style.load', register);

        if (!interactionsEnabled) {
            return () => {
                mlMap.off('style.load', register);
            };
        }

        const setPointer = () => {
            mlMap.getCanvas().style.cursor = 'pointer';
        };
        const clearPointer = () => {
            mlMap.getCanvas().style.cursor = '';
        };

        const makeClusterClickHandler = (layerId: string, sourceId: string) => (e: maplibregl.MapMouseEvent) => {
            const features = mlMap.queryRenderedFeatures(e.point, { layers: [layerId] });
            if (!features.length) return;
            const clusterId = features[0].properties['cluster_id'] as number | undefined;
            if (clusterId == null) return;
            const src = mlMap.getSource(sourceId) as maplibregl.GeoJSONSource;
            void src
                .getClusterExpansionZoom(clusterId)
                .then((zoom) => {
                    const geometry = features[0].geometry;
                    if (geometry.type !== 'Point') return;
                    mlMap.easeTo({ center: geometry.coordinates as [number, number], zoom });
                })
                .catch(() => {
                    // ignore cluster expansion errors
                });
        };
        const handleClusterClick = makeClusterClickHandler(ORBIT_LAYER_IDS.poiClusters, ENTITY_SOURCE_ID);
        const handleNetworkClusterClick = makeClusterClickHandler(
            ORBIT_LAYER_IDS.poiNetworkClusters,
            ENTITY_NETWORK_SOURCE_ID,
        );

        const handleNetworkSingleClick = (e: maplibregl.MapMouseEvent) => {
            const features = mlMap.queryRenderedFeatures(e.point, {
                layers: [ORBIT_LAYER_IDS.poiNetworkUnclustered],
            });
            if (!features.length) return;
            const geometry = features[0].geometry;
            if (geometry.type !== 'Point') return;
            mlMap.easeTo({ center: geometry.coordinates as [number, number], zoom: POI_MIN_ZOOM });
        };

        const pointerLayers = [
            ORBIT_LAYER_IDS.pois,
            ORBIT_LAYER_IDS.leaks,
            ORBIT_LAYER_IDS.poiNetworkUnclustered,
            ORBIT_LAYER_IDS.poiClusters,
            ORBIT_LAYER_IDS.poiNetworkClusters,
        ];
        for (const layerId of pointerLayers) {
            mlMap.on('mousemove', layerId, setPointer);
            mlMap.on('mouseleave', layerId, clearPointer);
        }
        mlMap.on('click', ORBIT_LAYER_IDS.poiClusters, handleClusterClick);
        mlMap.on('click', ORBIT_LAYER_IDS.poiNetworkClusters, handleNetworkClusterClick);
        mlMap.on('click', ORBIT_LAYER_IDS.poiNetworkUnclustered, handleNetworkSingleClick);

        return () => {
            mlMap.off('style.load', register);
            for (const layerId of pointerLayers) {
                mlMap.off('mousemove', layerId, setPointer);
                mlMap.off('mouseleave', layerId, clearPointer);
            }
            mlMap.off('click', ORBIT_LAYER_IDS.poiClusters, handleClusterClick);
            mlMap.off('click', ORBIT_LAYER_IDS.poiNetworkClusters, handleNetworkClusterClick);
            mlMap.off('click', ORBIT_LAYER_IDS.poiNetworkUnclustered, handleNetworkSingleClick);
        };
    }, [enabled, interactionsEnabled, isMapLoaded, mapRef, processViewport, processNetwork]);

    // Selected-entity highlight: shared id filter across the pin + halo layers, plus the
    // halo translate so an offset stack member's halos follow its fan position.
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map) return;
        for (const layerId of SELECTED_ENTITY_LAYER_IDS) {
            if (map.getLayer(layerId)) {
                map.setFilter(
                    layerId,
                    selectedEntityId ? ['==', ['get', 'id'], selectedEntityId] : ['==', ['get', 'id'], ''],
                );
            }
        }
        const translate = selectedEntityId
            ? findStackTranslate([viewportVisibleRef.current, networkVisibleRef.current], selectedEntityId)
            : ([0, 0] as [number, number]);
        for (const layerId of HALO_LAYER_IDS) {
            if (map.getLayer(layerId)) map.setPaintProperty(layerId, 'circle-translate', translate);
        }
    }, [selectedEntityId, mapRef]);

    // Selection halo ripple, identical behaviour to the pre-split pins. No cleanup stop:
    // StrictMode would restart it mid-flight; the pulse self-terminates and its paint
    // writes are guarded.
    useEffect(() => {
        if (!isMapLoaded) return;
        const map = mapRef.current?.getMap();
        if (!map) return;

        if (selectedEntityId == null) {
            haloPlayedForRef.current = null;
            return;
        }
        if (selectedEntityId === haloPlayedForRef.current) return;
        haloPlayedForRef.current = selectedEntityId;

        haloPulseRef.current?.stop();
        haloPulseRef.current = null;

        const reducedMotion =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reducedMotion) {
            if (map.getLayer(ORBIT_LAYER_IDS.poiSelectedHaloInner)) {
                map.setPaintProperty(ORBIT_LAYER_IDS.poiSelectedHaloInner, 'circle-radius', HALO_INNER_RADIUS);
            }
            if (map.getLayer(ORBIT_LAYER_IDS.poiSelectedHaloOuter)) {
                map.setPaintProperty(ORBIT_LAYER_IDS.poiSelectedHaloOuter, 'circle-radius', HALO_OUTER_RADIUS);
            }
            return;
        }

        haloPulseRef.current = createHaloPulse(map, {
            innerLayerId: ORBIT_LAYER_IDS.poiSelectedHaloInner,
            outerLayerId: ORBIT_LAYER_IDS.poiSelectedHaloOuter,
            minRadius: HALO_PULSE_MIN_RADIUS,
            maxRadius: HALO_PULSE_MAX_RADIUS,
            peakOpacity: HALO_PULSE_PEAK_OPACITY,
            cycleMs: HALO_PULSE_CYCLE_MS,
            overlapMs: HALO_PULSE_OVERLAP_MS,
            pulses: HALO_PULSE_PULSES,
            settleMs: HALO_PULSE_SETTLE_MS,
            innerSettleRadius: HALO_INNER_RADIUS,
            innerSettleOpacity: HALO_INNER_OPACITY,
            outerSettleRadius: HALO_OUTER_RADIUS,
            outerSettleOpacity: HALO_OUTER_OPACITY,
        });
        haloPulseRef.current.play();
    }, [isMapLoaded, selectedEntityId, mapRef]);
}

export const ENTITY_MARKER_DIMENSIONS = {
    width: MARKER_WIDTH,
    standardHeight: STANDARD_MARKER_HEIGHT,
    badgedHeight: BADGED_MARKER_HEIGHT,
    selectedWidth: SELECTED_MARKER_WIDTH,
    selectedHeight: SELECTED_MARKER_HEIGHT,
} as const;
